import _slicedToArray from "/home/highlander/projects/token-manager/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "/home/highlander/projects/token-manager/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/defineProperty";
import { path, prop } from 'ramda';
import { SLICE_VALUE_ERROR } from './errors';
import { id, isSliceValid, isPrimitive, isFunction, selectIfFunction, getSelectorName, getActionCreatorName, getType } from './helpers';

/**
 * Create action creator for given slice (with optional payload mapper).
 */
var createActionCreator = function createActionCreator(slice, name, mapPayload) {
  var type = getType(slice, name);
  return Object.assign(function () {
    return {
      type: type,
      payload: isFunction(mapPayload) ? mapPayload.apply(void 0, arguments) : arguments.length <= 0 ? undefined : arguments[0]
    };
  }, {
    type: type
  });
};

/**
 * Create reducer for given key from initial state.
 */
var createReducer = function createReducer(key) {
  return function (state, payload) {
    return !key && isPrimitive(payload) ? payload : Object.assign({}, state, key ? _defineProperty({}, key, payload) : payload);
  };
};

/**
 * Create selector for given slice.
 */
var createSelector = function createSelector(slice, fn) {
  return function (state) {
    return fn(state[slice], state);
  };
};

/**
 * Create default action creators, reducers and selectors.
 */
var createDefaults = function createDefaults(slice, initial) {
  var sliceActionCreatorName = getActionCreatorName(slice);
  return Object.keys(initial).reduce(function (_ref2, key) {
    var _ref3 = _slicedToArray(_ref2, 3),
      actionCreators = _ref3[0],
      reducers = _ref3[1],
      selectors = _ref3[2];
    var actionCreatorName = getActionCreatorName(key);
    return [Object.assign(actionCreators, _defineProperty({}, actionCreatorName, createActionCreator(slice, actionCreatorName))), Object.assign(reducers, _defineProperty({}, actionCreatorName, createReducer(key))), Object.assign(selectors, _defineProperty({}, getSelectorName(key), createSelector(slice, function (state) {
      return prop(key, state);
    })))];
  }, [_defineProperty({}, sliceActionCreatorName, createActionCreator(slice, sliceActionCreatorName)), _defineProperty({}, sliceActionCreatorName, createReducer()), _defineProperty({}, getSelectorName(slice), createSelector(slice, id))]);
};

/**
 * Produce action creators from user-defined 'actions' object.
 */
var createActionCreators = function createActionCreators(slice, actions) {
  return Object.keys(actions).reduce(function (obj, action) {
    return Object.assign({}, obj, _defineProperty({}, action, createActionCreator(slice, action, actions[action].create)));
  }, {});
};

/**
 * Produce selectors from user-defined 'selectors' object.
 */
var createSelectors = function createSelectors(slice, selectors) {
  return Object.keys(selectors).reduce(function (obj, key) {
    return Object.assign(obj, _defineProperty({}, key, createSelector(slice, selectors[key])));
  }, {});
};

/**
 * Validate options supplied to 'autodux'.
 */
var checkOptions = function checkOptions(_ref7) {
  var slice = _ref7.slice;
  if (!isSliceValid(slice)) {
    throw new Error(SLICE_VALUE_ERROR);
  }
};
export default function autodux() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  checkOptions(options);
  var _options$initial = options.initial,
    initial = _options$initial === void 0 ? '' : _options$initial,
    _options$actions = options.actions,
    actions = _options$actions === void 0 ? {} : _options$actions,
    _options$selectors = options.selectors,
    selectors = _options$selectors === void 0 ? {} : _options$selectors,
    slice = options.slice;
  var _createDefaults = createDefaults(slice, initial),
    _createDefaults2 = _slicedToArray(_createDefaults, 3),
    defaultActionCreators = _createDefaults2[0],
    defaultReducers = _createDefaults2[1],
    defaultSelectors = _createDefaults2[2];
  var allSelectors = Object.assign({}, defaultSelectors, createSelectors(slice, selectors));
  var allActions = Object.assign({}, defaultActionCreators, createActionCreators(slice, actions));
  var rootReducer = function rootReducer() {
    var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : initial;
    var _ref8 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref8$type = _ref8.type,
      type = _ref8$type === void 0 ? getType('unknown', 'unknown') : _ref8$type,
      payload = _ref8.payload;
    var _type$split = type.split('/'),
      _type$split2 = _slicedToArray(_type$split, 2),
      namespace = _type$split2[0],
      subType = _type$split2[1]; // Look for reducer with top-to-bottom precedence.
    // Fall back to default actions, then undefined.
    // 'actions[subType]' key can be a function or an object,
    // so the value is selected only if it's a function:
    var reducer = [path([subType, 'reducer'], actions), actions[subType], defaultReducers[subType]].reduceRight(function (f, v) {
      return selectIfFunction(v) || f;
    });
    return namespace === slice && (actions[subType] || defaultReducers[subType]) ? reducer ? reducer(state, payload) : Object.assign({}, state, payload) : state;
  };
  return {
    initial: initial,
    reducer: rootReducer,
    slice: slice,
    selectors: allSelectors,
    actions: allActions
  };
}

/**
 * Create reducer that assigns payload to the given key.
 */
export var assign = function assign(key) {
  return function (state, payload) {
    return Object.assign({}, state, _defineProperty({}, key, payload));
  };
};